import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiCheck } from '@mdi/js'
import { ImmeubleSection } from './ImmeubleSection'
import { LotsSection } from './LotsSection'
import { SpacesSection } from './SpacesSection'
import { useImmeubleReliability } from './useReliability'
import { useLots } from './useLots'
import { useSpaces } from './useSpaces'
import { useAggregatedReliabilityCount } from './useAggregatedCount'

const PC_LEVELS = ['Toiture', 'Niveaux courants', 'RDC', 'Sous-sol']

type SectionId = 'immeuble' | 'lots' | 'common' | 'exterior'

type NavItem = {
  id: SectionId
  label: string
  /** pending count; null => section not wired yet (no badge) */
  count: number | null
}

/**
 * Entry point of the Fiabilisation module. Renders identically regardless of
 * caller (building tab or onboarding final step) — the surrounding chrome is
 * owned by the caller. Owns the left section-nav, the aggregated counter
 * (spec §9) and dispatches to the active section.
 */
export function DataReliabilityTab({ org, assetUid }: { org: string; assetUid: string }) {
  const { t, i18n } = useTranslation('data-reliability')
  const locale = i18n.language || 'en'
  const [section, setSection] = useState<SectionId>('immeuble')

  // Counts come from the same hooks the sections use (TanStack Query dedupes).
  const { changes, isLoading: immeubleLoading } = useImmeubleReliability(org, assetUid, locale)
  const { lots, isLoading: lotsLoading } = useLots(org, assetUid)
  const { spaces: commonSpaces, isLoading: commonLoading } = useSpaces(org, assetUid, 'common')
  const { spaces: exteriorSpaces, isLoading: exteriorLoading } = useSpaces(org, assetUid, 'exterior')
  const allCountsLoaded = !immeubleLoading && !lotsLoading && !commonLoading && !exteriorLoading
  const lotsToReview = useMemo(() => lots.filter((l) => l.pending).length, [lots])
  const commonToReview = useMemo(() => commonSpaces.filter((s) => s.pending).length, [commonSpaces])
  const exteriorToReview = useMemo(() => exteriorSpaces.filter((s) => s.pending).length, [exteriorSpaces])

  const nav: NavItem[] = useMemo(
    () => [
      { id: 'immeuble', label: t('sections.immeuble'), count: changes.length },
      { id: 'lots', label: t('sections.lots'), count: lots.length > 0 ? lotsToReview : null },
      { id: 'common', label: t('sections.common'), count: commonSpaces.length > 0 ? commonToReview : null },
      { id: 'exterior', label: t('sections.exterior'), count: exteriorSpaces.length > 0 ? exteriorToReview : null },
    ],
    [t, changes.length, lots.length, lotsToReview, commonSpaces.length, commonToReview, exteriorSpaces.length, exteriorToReview],
  )

  // Shared with the "Data" tab's nav-entry badge (AssetDetailPage) so both
  // always agree — this local `nav.reduce` sum happens to equal it (same
  // section counts), but the shared hook is the single source of truth.
  const aggregated = useAggregatedReliabilityCount(org, assetUid, locale)

  // Spec §9: "Clicking [the aggregated counter] routes to the first
  // non-empty section." Since this module always renders from the same
  // entry point the counter routes to, the equivalent behaviour is: land on
  // the first section with a pending count on initial load, instead of
  // hardcoding 'immeuble'. Waits for every section's own query to finish
  // loading before picking — otherwise an early empty-array render (before
  // TanStack Query resolves) can win the race and lock in the wrong
  // section, since this only ever runs once per mount. A manual section
  // click afterwards is never overridden.
  const autoSelected = useRef(false)
  useEffect(() => {
    if (autoSelected.current || !allCountsLoaded) return
    const firstNonEmpty = nav.find((n) => (n.count ?? 0) > 0)
    if (firstNonEmpty) {
      autoSelected.current = true
      setSection(firstNonEmpty.id)
    }
  }, [nav, allCountsLoaded])

  return (
    <div className="@container" data-testid="data-reliability-tab">
      {/* aggregated counter (spec §9) */}
      <div className="mb-5">
        {aggregated > 0 ? (
          <span
            className="inline-flex items-center gap-2 rounded-full bg-warning/15 px-3 py-1 text-sm font-semibold text-warning"
            data-testid="dr-aggregated-count"
          >
            {t('aggregated_to_review', { count: aggregated })}
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-2 rounded-full bg-success/10 px-3 py-1 text-sm font-semibold text-success"
            data-testid="dr-aggregated-count"
          >
            <AppIcon path={mdiCheck} size="sm" />
            {t('all_reliable')}
          </span>
        )}
      </div>

      <div className="grid gap-6 @3xl:grid-cols-[190px_1fr]">
        {/* section nav — horizontal chips when narrow, vertical list when wide */}
        <nav>
          <div className="mb-3 hidden text-[11px] font-bold tracking-wide text-muted-foreground @3xl:block">{t('sections_label')}</div>
          <div className="flex flex-row flex-wrap gap-1 @3xl:flex-col">
            {nav.map((n) => {
              const active = section === n.id
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => setSection(n.id)}
                  className={`flex w-auto items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors @3xl:w-full ${
                    active
                      ? 'border-primary bg-primary/10 font-medium text-foreground'
                      : 'border-transparent text-muted-foreground hover:bg-muted/50'
                  }`}
                  data-testid={`dr-nav-${n.id}`}
                >
                  {n.label}
                  {n.count != null && (
                    <span
                      className={`min-w-5 rounded-full px-1.5 text-center text-xs font-bold ${
                        n.count > 0 ? 'bg-warning/15 text-warning' : 'text-success'
                      }`}
                    >
                      {n.count > 0 ? n.count : <AppIcon path={mdiCheck} size="xs" className="inline" />}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </nav>

        {/* active section */}
        <div>
          {section === 'immeuble' && <ImmeubleSection org={org} assetUid={assetUid} locale={locale} />}
          {section === 'lots' && <LotsSection org={org} buildingUid={assetUid} />}
          {section === 'common' && (
            <SpacesSection org={org} buildingUid={assetUid} kind="common" title={t('spaces.common_title')} desc={t('spaces.common_desc')} levels={PC_LEVELS} />
          )}
          {section === 'exterior' && (
            <SpacesSection org={org} buildingUid={assetUid} kind="exterior" title={t('spaces.exterior_title')} desc={t('spaces.exterior_desc')} />
          )}
        </div>
      </div>
    </div>
  )
}
